<template>
  <form class="bwauth-form" novalidate @submit.prevent="submit">
    <div v-if="generalError" class="bwauth-alert bwauth-alert-error">
      {{ generalError }}
    </div>

    <label class="bwauth-field">
      <span>{{ $t('Email') }}</span>
      <input
        v-model.trim="form.email"
        type="email"
        name="email"
        autocomplete="email"
        :class="{ 'has-error': fieldError('email') }"
        @blur="touch('email')"
      >
      <small v-if="fieldError('email')">{{ fieldError('email') }}</small>
    </label>

    <label class="bwauth-field">
      <span>{{ $t('Password') }}</span>
      <div class="bwauth-password-field">
        <input
          v-model="form.password"
          :type="showPassword ? 'text' : 'password'"
          name="password"
          autocomplete="current-password"
          :class="{ 'has-error': fieldError('password') }"
          @blur="touch('password')"
        >
        <password-toggle v-model="showPassword" />
      </div>
      <small v-if="fieldError('password')">{{ fieldError('password') }}</small>
    </label>

    <div class="bwauth-row bwauth-row-between">
      <a v-if="resetPasswordHref" class="bwauth-link" :href="resetPasswordHref" @click="switchToResetPassword">
        {{ $t('Reset Password') }}?
      </a>
    </div>

    <div class="bwauth-actions">
      <button type="submit" class="bwauth-primary-button" :disabled="loading">
        <span v-if="loading" class="bwauth-spinner"></span>
        <template v-else>{{ $t('Submit Login') }}</template>
      </button>
    </div>

    <div v-if="showRegisterLink" class="bwauth-switch-copy">
      {{ $t('No account yet?') }}
      <a class="bwauth-link" :href="registerHref" @click="switchToRegister">
        {{ $t('Register') }}
      </a>
    </div>

    <social-auth
      mode="login"
      :redirect-url="redirectUrl"
      @success="handleSocialSuccess"
      @error="handleSocialError"
    />
  </form>
</template>

<script>
import AuthApi from './AuthApi';
import SocialAuth from './SocialAuth.vue';
import PasswordToggle from './PasswordToggle.vue';
import { validationMixin } from 'vuelidate';
import { required, email, minLength } from 'vuelidate/lib/validators';

export default {
  mixins: [validationMixin],
  components: {
    SocialAuth,
    PasswordToggle,
  },
  props: {
    initialEmail: {
      type: String,
      default: '',
    },
    redirectUrl: {
      type: String,
      default: null,
    },
    resetPasswordHref: {
      type: String,
      default: '/reset-password',
    },
    registerHref: {
      type: String,
      default: '/register',
    },
    showRegisterLink: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      form: {
        email: this.initialEmail,
        password: '',
      },
      touched: {},
      errors: {},
      loading: false,
      showPassword: false,
    };
  },
  validations: {
    form: {
      email: { required, email },
      password: { required, minLength: minLength(6) },
    },
  },
  computed: {
    generalError() {
      if (this.errors && this.errors.message) {
        return this.errors.message;
      }
      return null;
    },
  },
  methods: {
    touch(field) {
      if (this.$v && this.$v.form && this.$v.form[field]) {
        this.$v.form[field].$touch();
      }
    },
    fieldError(field) {
      const control = this.$v && this.$v.form ? this.$v.form[field] : null;
      if (control && control.$dirty && control.$invalid) {
        if (!control.required) {
          return this.$t('validation.required');
        }
        if (field === 'email' && !control.email) {
          return this.$t('validation.email');
        }
        if (field === 'password' && !control.minLength) {
          return this.$t('validation.txtMinLen', { n: control.$params.minLength.min });
        }
      }
      if (this.errors && this.errors[field]) {
        return Array.isArray(this.errors[field]) ? this.errors[field][0] : this.errors[field];
      }
      return null;
    },
    isValid() {
      this.$v.form.$touch();
      return !this.$v.form.$invalid;
    },
    handleSocialSuccess(user) {
      this.$emit('success', user);
    },
    handleSocialError(error) {
      const response = error && error.response && error.response.data ? error.response.data : {};
      this.errors = response.errors || { message: response.message || this.$t('Server error') };
    },
    submit() {
      this.errors = {};

      if (!this.isValid()) {
        return;
      }

      this.loading = true;
      this.$emit('loading', true);

      AuthApi.login({
        ...this.form,
        redirect_url: this.redirectUrl,
      })
        .then(({ data }) => {
          this.$emit('success', data.user || data);
        })
        .catch((error) => {
          const response = error.response && error.response.data ? error.response.data : {};
          this.errors = response.errors || { message: response.message || this.$t('Server error') };
        })
        .finally(() => {
          this.loading = false;
          this.$emit('loading', false);
        });
    },
    switchToRegister(event) {
      if (this.$listeners['switch-to-register'] && event) {
        event.preventDefault();
      }
      this.$emit('switch-to-register');
    },
    switchToResetPassword(event) {
      if (this.$listeners['switch-to-reset-password'] && event) {
        event.preventDefault();
      }
      this.$emit('switch-to-reset-password');
    },
  },
};
</script>
